import React, { useCallback, useEffect, useMemo } from 'react'
import { Form, Row, Spin } from 'antd'
import { isEmpty } from 'lodash'

import {
  DirtyPromptForm,
  FormButtons,
  FormImageUpload,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'

import { useUpdateLocationTypeImageDetailsMutation } from '../../hooks'
import { locationTypePropType } from '../../prop-types'

import { locationTypeImageFormInitialValues } from './initial-values'

const LocationTypeImageDetailsForm = ({ locationType }) => {
  const [form] = Form.useForm()

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.LOCATION_TYPE.UPDATE_IMAGE_DETAILS,
  ])

  const [
    updateLocationTypeImageDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateLocationTypeImageDetailsMutation()

  const initialValues = useMemo(
    () => locationTypeImageFormInitialValues(locationType),
    [locationType],
  )

  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const handleSaveData = useCallback(
    data => updateLocationTypeImageDetails(locationType.id, data),
    [locationType.id, updateLocationTypeImageDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Location Type details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [locationType.imageUrl, locationType.iconUrl, resetForm])

  return isSaveLoading || isEmpty(initialValues) ? (
    <Spin />
  ) : (
    <>
      <DirtyPromptForm
        isDirty={isDirty}
        form={form}
        initialValues={initialValues}
        name="locationTypeImageForm"
        onFinish={handleSaveData}
        autoComplete="off"
        onFieldsChange={setToDirty}
      >
        <Row>
          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="image"
            label="Image"
          />
          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="icon"
            label="Icon"
          />
        </Row>
        <FormButtons
          permissions={[PERMISSIONS.LOCATION_TYPE.UPDATE_IMAGE_DETAILS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </DirtyPromptForm>
    </>
  )
}

LocationTypeImageDetailsForm.propTypes = {
  locationType: locationTypePropType.isRequired,
}

export default LocationTypeImageDetailsForm
